import { COLORS } from '../../../../../shared/utils/colors.constant';
import { BOOKING_STATUS } from '../../domain/enum/booking-status.enum';
import { BOOKING_SPACE_STATUS } from '../../domain/enum/space-status.enum';
import { IApiBooking } from "../interfaces/booking-api.interface";
import { BookingMapper } from "../mappers/booking.mapper";

/**
 * Mock for a confirmed booking
 * BookingPrice: 140€ (includes 30€ cover charge)
 * A consumir: 140€ - 30€ = 110€
 * Current consumption: 50€
 * Pending consumption: 110€ - 50€ = 60€
 * Paid: 50€
 * To be paid: 140€ + 50€ - 50€ = 140€
 */
export const bookingMock1: IApiBooking = {
  id: 'b001',
  status: BOOKING_STATUS.confirmed,
  accountId: '',
  customer: {
    id: 'cust001',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1234567890',
    country: 'España',
  },
  isActive: true,
  name: 'Reserva VIP',
  spaces: [
    {
      _id: 'space001',
      organization_id: 'org001',
      zone_id: 'zone001',
      number: 1,
      name: 'Mesa VIP',
      description: 'Mesa VIP con excelente ubicación',
      capacity: 4,
      status: BOOKING_SPACE_STATUS.OCCUPIED,
      rate_ids: ['rate001'],
      created_at: Date.now() - 30 * 24 * 60 * 60 * 1000,
      created_by: 'admin',
      updated_at: Date.now() - 7 * 24 * 60 * 60 * 1000,
      updated_by: 'user001'
    },
  ],
  zone: {
    id: 'zone001',
    name: 'Zona VIP',
    color: COLORS.purple,
    rates: [],
  },
  date: '2024-08-15',
  startTime: '20:00',
  endTime: '23:00',
  partySize: 4,
  lockedBy: '',
  financial: {
    currency: 'EUR',
    bookingPrice: 240,
    deposit: 100,
    paid: 100,
    toBePaid: 260,
    coverCharge: {
      enabled: true,
      total: 40,
    },
    currentConsumption: 0,
    pendingConsumption: 80,
    total: 360,
  },
  notes: 'Reserva para aniversario',
  bookerNotes: 'Clientes VIP, ofrecer mesa con vista',
  referredBy: {
    id: 'ref001',
    name: 'Instagram',
  },
  isClosed: false,
  createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000, // 7 days ago
  createdBy: 'user001',
  updatedAt: Date.now() - 2 * 24 * 60 * 60 * 1000, // 2 days ago
  updatedBy: 'user001'
};

/**
 * Mock for a seated booking
 * BookingPrice: 240€ (includes 40€ cover charge)
 * A consumir: 240€ - 40€ = 200€
 * Current consumption: 120€
 * Pending consumption: 200€ - 120€ = 80€
 * Paid: 100€
 * To be paid: 240€ + 120€ - 100€ = 260€
 */
export const bookingMock2: IApiBooking = {
  id: 'b002',
  status: BOOKING_STATUS.accepted,
  accountId: '',
  customer: {
    id: 'cust002',
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    phone: '+0987654321',
    country: 'España',
  },
  isActive: true,
  lockedBy: 'me',
  name: 'Reserva VIP 4',
  spaces: [
    {
      _id: 'space002',
      organization_id: 'org001',
      zone_id: 'zone002',
      number: 2,
      name: 'Mesa Terraza',
      description: 'Mesa en terraza con vista panorámica',
      capacity: 6,
      status: BOOKING_SPACE_STATUS.OCCUPIED,
      rate_ids: ['rate002'],
      created_at: Date.now() - 30 * 24 * 60 * 60 * 1000,
      created_by: 'admin',
      updated_at: Date.now() - 10 * 24 * 60 * 60 * 1000,
      updated_by: 'user001'
    },
  ],
  zone: {
    id: 'zone002',
    name: 'Terraza',
    color: COLORS.teal,
    rates: [],
  },
  date: '2024-08-10',
  startTime: '21:00',
  endTime: '01:00',
  partySize: 6,
  financial: {
    currency: 'EUR',
    bookingPrice: 100,
    deposit: 50,
    paid: 50,
    toBePaid: 75,
    coverCharge: {
      enabled: false,
      total: 0,
    },
    currentConsumption: 0,
    pendingConsumption: 75,
    total: 125,
  },
  notes: 'Reserva para cumpleaños',
  bookerNotes: 'Traerán tarta, preparar espacio',
  referredBy: {
    id: 'ref002',
    name: 'Facebook',
  },
  isClosed: false,
  createdAt: Date.now() - 10 * 24 * 60 * 60 * 1000, // 10 days ago
  createdBy: 'user001',
  updatedAt: Date.now() - 1 * 24 * 60 * 60 * 1000, // 1 day ago
  updatedBy: 'user002'
};

/**
 * Mock for a closed booking
 * BookingPrice: 150€ (includes 20€ cover charge)
 * A consumir: 150€ - 20€ = 130€
 * Current consumption: 130€ (fully consumed)
 * Pending consumption: 0€
 * Paid: 150€ (fully paid)
 * To be paid: 0€
 */
export const bookingMock3: IApiBooking = {
  id: 'b003',
  status: BOOKING_STATUS.confirmed,
  accountId: '',
  customer: {
    id: 'cust003',
    name: 'Carlos Rodriguez',
    email: 'carlos.rodriguez@example.com',
    phone: '+3467891234',
    country: 'España',
  },
  isActive: false,
  name: 'Reserva Ejecutiva',
  spaces: [
    {
      _id: 'space003',
      organization_id: 'org001',
      zone_id: 'zone003',
      number: 3,
      name: 'Mesa Ejecutiva',
      description: 'Mesa privada para reuniones de trabajo',
      capacity: 2,
      status: BOOKING_SPACE_STATUS.AVAILABLE,
      rate_ids: ['rate003'],
      created_at: Date.now() - 60 * 24 * 60 * 60 * 1000,
      created_by: 'admin',
      updated_at: Date.now() - 3 * 24 * 60 * 60 * 1000,
      updated_by: 'user001'
    },
  ],
  zone: {
    id: 'zone003',
    name: 'Zona Ejecutiva',
    color: COLORS.zinc,
    rates: [],
  },
  date: '2024-08-05',
  startTime: '13:00',
  endTime: '15:00',
  partySize: 2,
  lockedBy: '',
  financial: {
    currency: 'EUR',
    bookingPrice: 150,
    deposit: 75,
    paid: 280,
    toBePaid: 0,
    coverCharge: {
      enabled: true,
      total: 20,
    },
    currentConsumption: 130,
    pendingConsumption: 0,
    total: 280,
  },
  notes: 'Reunión de trabajo - Completada',
  bookerNotes: 'Cliente habitual, servicio excelente prestado',
  referredBy: {
    id: 'ref003',
    name: 'LinkedIn',
  },
  isClosed: true,
  createdAt: Date.now() - 15 * 24 * 60 * 60 * 1000, // 15 days ago
  createdBy: 'user001',
  updatedAt: Date.now() - 3 * 24 * 60 * 60 * 1000, // 3 days ago
  updatedBy: 'user001'
};

/**
 * Collection of all booking mocks
 */
export const bookingMocks: IApiBooking[] = [
  bookingMock1,
  bookingMock2,
  bookingMock3,
];

/**
 * Collection of active booking mocks (not cancelled or no-show)
 */
export const activeBookingMocks: IApiBooking[] = bookingMocks.filter(
  (booking) =>
    booking.status !== BOOKING_STATUS.cancelled &&
    booking.status !== BOOKING_STATUS.noShow
);

/**
 * Collection of pending booking mocks
 */
export const pendingBookingMocks: IApiBooking[] = bookingMocks.filter(
  (booking) => booking.status === BOOKING_STATUS.pending
);

/**
 * Collection of confirmed booking mocks
 */
export const confirmedBookingMocks: IApiBooking[] = bookingMocks.filter(
  (booking) => booking.status === BOOKING_STATUS.confirmed
);

/**
 * Collection of closed booking mocks
 */
export const closedBookingMocks: IApiBooking[] = bookingMocks.filter(
  (booking) => booking.isClosed === true
);

/**
 * Collection of open (not closed) booking mocks
 */
export const openBookingMocks: IApiBooking[] = bookingMocks.filter(
  (booking) => booking.isClosed === false
);

/**
 * Creates Booking class instances from mock data
 */
export const bookingMockInstances = bookingMocks.map((mock) =>
  BookingMapper.fromJSON(mock)
);
